import { __decorate } from "tslib";
import { ActivityEventType } from '@metaos/mfs-telemetry';
import { mfsErrorHandler } from '../errors/mfsErrorHandler';
import { CardPropertyUtils } from '../utility/cardPropertyUtils';
import { ConfigurationManager } from '../utility/configurationManager';
import { logActivity, logger } from '../utility/telemetry';
import { AdministrationClient } from './administrationClient';
import { CardClient } from './cardClient';
import { EventClient } from './eventClient';
import { TagClient } from './tagClient';
import { ViewableClient } from './viewableClient';
/** Concrete implementation for Folders client.*/
export class FoldersClient {
    constructor(attachmentCallback) {
        this.attachmentCallback = attachmentCallback;
        this.isInitialized = attachmentCallback === undefined;
    }
    /** Not exposed in contract. Using static factory instead of constructor due to async code that cannot be
     * ran in constructors */
    static async initialize(datastore, clearCacheFunc, attachmentCallback) {
        const client = new FoldersClient(attachmentCallback);
        client.cardPropertyUtils = new CardPropertyUtils(datastore);
        client.configurationManager = new ConfigurationManager();
        client.cardClient = new CardClient(datastore, client.cardPropertyUtils);
        client.eventClient = new EventClient(datastore, client.cardClient);
        client.viewableClient = new ViewableClient(datastore);
        client.administrationClient = new AdministrationClient(clearCacheFunc);
        client.tagClient = await TagClient.initialize(datastore, client.cardPropertyUtils, client.configurationManager);
        return client;
    }
    /** @inheritdoc */
    // Log Activity decorator is set inside the individual views.
    viewBy(viewKind, criteria) {
        return this.viewableClient.viewBy(viewKind, criteria);
    }
    /** @inheritdoc */
    onScopedEvent(cardId, scopedEventType, callback) {
        return this.eventClient.onScopedEvent(cardId, scopedEventType, callback);
    }
    /** @inheritdoc */
    onUnscopedEvent(event, callback) {
        return this.eventClient.onUnscopedEvent(event, callback);
    }
    /** @inheritdoc */
    async createCard(card, ensurePersistence = true) {
        await this.cardClient.createCard(card);
        this.ensureAttachment(ensurePersistence);
    }
    /**
     * Ensures that the dataStore's pod is attached.
     * If the client is not initialized, it will call the attachment callback to initialize it.
     * @param ensurePersistence User-supplied flag to indicate whether to ensure persistence.
     */
    ensureAttachment(ensurePersistence) {
        if (!ensurePersistence || this.isInitialized || !this.attachmentCallback)
            return;
        // Set isInitialized to true before calling attachmentCallback to prevent multiple attachment attempts at once.
        this.isInitialized = true;
        this.attachmentCallback()
            .then(() => {
            logger.logVerbose('attachmentSuccess', 'pod attached successfully');
        })
            .catch((error) => {
            // Do not set isInitialized to false here, as it is possible that a file could be created but the rest of attachment fails, and we do not
            // want to accidentally create multiple files.
            const err = mfsErrorHandler('Error occurred while trying to attach pod', error, 'ensureAttachment');
            logger.logError('attachmentError', err.mfsStatusCode, { error: err }, err.eventName);
        });
    }
    /** @inheritdoc */
    patchCard(cardId, properties) {
        return this.cardClient.patchCard(cardId, properties);
    }
    /** @inheritdoc */
    getCard(cardId) {
        return this.cardClient.getCard(cardId);
    }
    /** @inheritdoc */
    addCard(cardId, parentCardId) {
        return this.cardClient.addCard(cardId, parentCardId);
    }
    /** @inheritdoc */
    getCards(query) {
        return this.cardClient.getCards(query);
    }
    /** @inheritdoc */
    removeFromParent(cardId, parentCardId) {
        return this.cardClient.removeFromParent(cardId, parentCardId);
    }
    /** @inheritdoc */
    deleteCard(cardId) {
        return this.cardClient.deleteCard(cardId);
    }
    /** @inheritdoc */
    tag(cardId, tagId) {
        return this.tagClient.tag(cardId, tagId);
    }
    /** @inheritdoc */
    untag(cardId, tagId) {
        return this.tagClient.untag(cardId, tagId);
    }
    /** @inheritdoc */
    async createTag(tag, ensurePersistence = true) {
        const tagId = await this.tagClient.createTag(tag);
        this.ensureAttachment(ensurePersistence);
        return tagId;
    }
    /** @inheritdoc */
    patchTag(tagId, tagPatch) {
        return this.tagClient.patchTag(tagId, tagPatch);
    }
    /** @inheritdoc */
    getTag(tagId) {
        return this.tagClient.getTag(tagId);
    }
    /** @inheritdoc */
    getTagByValue(tagValue) {
        return this.tagClient.getTagByValue(tagValue);
    }
    /** @inheritdoc */
    getTags(query) {
        return this.tagClient.getTags(query);
    }
    /** @inheritdoc */
    deleteTag(tagId) {
        return this.tagClient.deleteTag(tagId);
    }
    /** @inheritdoc */
    clearCache() {
        return this.administrationClient.clearCache();
    }
}
__decorate([
    logActivity(ActivityEventType.NONE)
], FoldersClient.prototype, "onScopedEvent", null);
__decorate([
    logActivity(ActivityEventType.NONE)
], FoldersClient.prototype, "onUnscopedEvent", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "createCard", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "patchCard", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "getCard", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "addCard", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "getCards", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "removeFromParent", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "deleteCard", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "tag", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "untag", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "createTag", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "patchTag", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "getTag", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "getTagByValue", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "getTags", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "deleteTag", null);
__decorate([
    logActivity(ActivityEventType.SINGLE)
], FoldersClient.prototype, "clearCache", null);
//# sourceMappingURL=foldersClient.js.map